import { useEffect, useRef } from 'react'
import type { JSX } from 'react'

import type { ChannelType } from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'

import { AccessDenied } from '~publish/components/AccessDenied'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

type AccessDeniedChannel = {
  service: ChannelType
  name: string
  avatar?: string
}

/**
 * Full-page state shown when the actor lacks `viewInsights` on the channel(s)
 * they're trying to open — so the Insights surface fails with an explicit
 * "Access Denied" rather than an empty "no data" view.
 *
 * Thin wrapper over the shared {@link AccessDenied} — it supplies the
 * Insights-specific copy and "back" link while inheriting the unified design
 * shared across Publish, Community, and Insights (CORE-6013).
 *
 * Pass `channel` on the single-channel view to name it and offer a way back to
 * the all-channels overview; omit it on the all-channels view (no channel is
 * viewable).
 */
export function InsightsAccessDenied({
  channel,
}: {
  channel?: AccessDeniedChannel
}): JSX.Element {
  const commonTrackingProperties = useCommonTrackingProperties()
  const hasTrackedView = useRef(false)

  // Record that the actor hit the Insights permission gate. It's a page view,
  // not an upgrade path — the denial is a role/permission restriction with no
  // upgrade action to take. Fired once per mount (the state remounts each time
  // it's shown).
  useEffect(() => {
    // Wait for org context so the event isn't locked in with an empty
    // organizationId (the ref permanently blocks re-firing).
    if (hasTrackedView.current || !commonTrackingProperties.organizationId)
      return
    hasTrackedView.current = true

    BufferTracker.pageViewed({
      name: 'insights-access-denied',
      product: Product.Publish,
      path: window.location.pathname,
      url: window.location.href,
      ...commonTrackingProperties,
    })
  }, [commonTrackingProperties])

  return (
    <AccessDenied
      channel={channel}
      description={`You don't have permission to view Insights${
        channel ? ` for ${channel.name}` : ''
      }. Ask an admin on your team to update your permissions.`}
      action={
        channel ? { to: '/insights', label: 'Back to Insights' } : undefined
      }
    />
  )
}
